import { useState } from 'react'
import { ServiceTimeKindType } from '@ancon/wildcat-types'
import { CheckoutRequestItem } from '@ancon/wildcat-utils/checkout/types'

import useAppDispatch from '../../../store/hooks/useAppDispatch'
import useAppStore from '../../../store/hooks/useAppStore'
import {
  checkoutCapacityConflictAllocationSelector,
  checkoutCapacityConflictModalStateSelector,
} from '../store/checkoutSelectors'
import {
  checkoutHideCapacityConflictModal,
  checkoutSetCheckoutFilters,
} from '../store/checkoutSlice'
import {
  createCheckout,
  deleteCheckout,
  fetchCurrentCheckoutDetails,
  updateCheckout,
} from '../store/checkoutThunks'
import {
  outletSetOrderConfigureFilterModal,
  outletSetOutletFilters,
} from '../../outlet/store/outletSlice'
import { OrderConfigureFilterMode } from '../../outlet/types'

/**
 * The ways out of the capacity conflict modal.
 *
 * The three that resolve it here re-submit the request that was refused - as a
 * create or an update, depending on which call hit the conflict - and close the
 * modal only once it succeeds. On another conflict the rejected case has already
 * reopened the modal with fresh data, so closing afterwards would throw that away.
 * The fourth hands the guest over to the day picker instead.
 */
export default function useCapacityConflictActions() {
  const dispatch = useAppDispatch()
  const store = useAppStore()

  const [isPending, setIsPending] = useState(false)

  function getModalState() {
    return checkoutCapacityConflictModalStateSelector(store.getState())
  }

  /** Re-sends the refused cart, as whichever call was refused. */
  async function submit(items: CheckoutRequestItem[]) {
    const modalState = getModalState()

    if (!modalState) {
      return false
    }

    const { origin, outletId } = modalState

    if (origin === 'update') {
      const result = await dispatch(updateCheckout({ items }))

      return result.meta.requestStatus === 'fulfilled'
    }

    const result = await dispatch(createCheckout({ items, outletId }))

    if (result.meta.requestStatus !== 'fulfilled') {
      return false
    }

    // Unlike updateCheckout, createCheckout doesn't fetch the checkout it just
    // made, so without this the modal closes onto an empty cart. Awaited so the
    // cart is right by the time it closes, but a failed fetch doesn't hold the
    // modal open - the checkout exists and that thunk reports its own errors.
    await dispatch(fetchCurrentCheckoutDetails())

    return true
  }

  async function run(action: () => Promise<boolean>) {
    setIsPending(true)

    try {
      if (await action()) {
        dispatch(checkoutHideCapacityConflictModal())
      }
    } finally {
      setIsPending(false)
    }
  }

  /** Keep the whole order, move it to a time the kitchen can make it. */
  function scheduleAt(time: string) {
    return run(async () => {
      const modalState = getModalState()

      if (!modalState) {
        return false
      }

      const serviceTime = {
        kind: ServiceTimeKindType.AtSpecifiedTime,
        time,
      }

      // Both stores, because the two re-submits read different ones: update takes
      // the checkout filters, create takes the outlet filters.
      dispatch(outletSetOutletFilters({ serviceTime }))
      dispatch(checkoutSetCheckoutFilters({ serviceTime }))

      return submit(modalState.requestedItems)
    })
  }

  /** Apply the quantities the guest settled on, dropping what they removed. */
  function applyAdjustment() {
    return run(async () => {
      const state = store.getState()
      const modalState = checkoutCapacityConflictModalStateSelector(state)
      const allocation = checkoutCapacityConflictAllocationSelector(state)

      if (!modalState || !allocation) {
        return false
      }

      const keptByLineId = new Map(
        allocation.groups
          .flatMap(group => group.rows)
          .map(row => [row.lineId, row.keptQuantity]),
      )

      // Quantities are applied to the original request items so modifications,
      // add-on products and upsell groups survive the round trip.
      const items = modalState.requestedItems.reduce<CheckoutRequestItem[]>(
        (kept, item, index) => {
          const lineId = `${item.variantId}-${index}`
          const quantity = keptByLineId.has(lineId)
            ? (keptByLineId.get(lineId) as number)
            : item.quantity

          if (quantity > 0) {
            kept.push({ ...item, quantity })
          }

          return kept
        },
        [],
      )

      return submit(items)
    })
  }

  /** Empty the cart. Only ever offered when a checkout actually exists. */
  function deleteOrder() {
    return run(async () => {
      const result = await dispatch(deleteCheckout())

      return result.meta.requestStatus === 'fulfilled'
    })
  }

  /**
   * Leave for the day/time picker. Offered when the outlet has nothing left at
   * all on the requested day, so there is no time to choose from in here.
   */
  function chooseAnotherDay() {
    dispatch(
      outletSetOrderConfigureFilterModal(OrderConfigureFilterMode.OrderTime),
    )
    dispatch(checkoutHideCapacityConflictModal())
  }

  /**
   * Backs out without resolving anything. The refused update already left the
   * checkout as the server last confirmed it - nothing local was applied - but a
   * re-fetch is cheap insurance against whichever optimistic UI triggered this
   * update not having rolled itself back yet. Only meaningful on the update path:
   * a refused create never had a checkout to fetch.
   */
  function cancel() {
    const modalState = getModalState()

    dispatch(checkoutHideCapacityConflictModal())

    if (modalState?.origin === 'update') {
      dispatch(fetchCurrentCheckoutDetails())
    }
  }

  return {
    isPending,
    scheduleAt,
    applyAdjustment,
    deleteOrder,
    chooseAnotherDay,
    cancel,
  }
}
